import React from 'react'
import deepEqual from 'fast-deep-equal'

export type DropdownValue = string | number | boolean | null

export interface DropdownOption {
    id?: DropdownValue
    name?: React.ReactNode
    label?: React.ReactNode
    icon?: string
    iconSize?: string | number
    desc?: React.ReactNode
    availableDestinationQ?: boolean
    nestedKey?: string
    children?: DropdownOption[]
    systemQ?: boolean
}

export type DropdownChangeValue = DropdownValue | DropdownOption | DropdownOption[]

type DropdownChangeHandler = {
    bivarianceHack(value: DropdownChangeValue, option?: DropdownOption | null, name?: string): void
}['bivarianceHack']

interface DropdownSearchableControl {
    focus(): void
}

export const getDropdownOptionField = (option: DropdownOption, field: string): unknown => {
    return (option as Record<string, unknown>)[field]
}

export interface IDropdownProps {
    onValidateSymbolNumber?: (isValidate: boolean) => void
    id: string
    placeholder?: string
    icon?: string
    className?: string
    containerClassName?: string | object
    value?: DropdownValue
    multiselect: boolean
    searchable: boolean
    notFilterOnSearch: boolean
    inheritIconColor: boolean
    disabled?: boolean
    options: DropdownOption[]
    optionsBackground?: string
    valueField: string
    labelField: string
    iconActiveColor?: string
    centered: boolean
    disableChangeRounded?: boolean
    iconColor?: string
    iconSize?: string | number
    noFocus: boolean
    textSize?: string
    mod: string
    label?: React.ReactNode
    children?: React.ReactNode
    customHeader?: React.ReactNode
    rounded: string
    indentSize: string
    labelClassName?: string
    mask?: string
    onChange: DropdownChangeHandler
    onChangeTimeFormat?: (format: string) => void
    onShow?: () => void
    onHide?: () => void
    onBlur?: (value: { value: string }, e?: React.FocusEvent<HTMLInputElement>) => void
    config?: { pattern?: RegExp }
    small?: boolean
    showIconOnHover?: boolean
    placeholderAsValue?: boolean
    hideNotFound: boolean
    wrapperWidth?: boolean
    onlyPlaceholder?: boolean
    disableAddNewTags: boolean
    active?: boolean
    multiselectItems?: boolean
    list?: DropdownOption[]
    type?: string
    validationBorder?: boolean
    isNotDropdown?: boolean
    hmsState?: {
        h?: boolean
        m?: boolean
        s?: boolean
    }
    buttonClassName?: string

    iconClassName?: string
    searchableInputClassName?: string
    isRelativeList?: boolean
    textMobPadding?: boolean
    hiddenOverflow?: boolean
    scheduleMode?: boolean
    customListElement?: (option: DropdownOption) => React.ReactNode
    dropDirection?: string
    showCustomOption?: boolean
    customButtonContent?: React.ReactNode
    disablePlaceholder?: boolean
}

interface IState {
    active: boolean
    inputValue: string
    selected: {
        id: DropdownValue | undefined
        name: React.ReactNode
    }
    selectedList: DropdownOption[] | null
    opt: DropdownOption[]
    position: 'top' | 'bottom'
    positionChanged: boolean
}

const getFilteredList = (options: DropdownOption[], list: DropdownOption[] | undefined) => {
    if (list) {
        return options.filter((field) => !list.map((listItem) => listItem.name).includes(field.name))
    } else {
        return options
    }
}

class DropdownMethods extends React.Component<IDropdownProps> {
    state: IState
    roundedMod: string = ''
    filtered: DropdownOption[]
    searchable: DropdownSearchableControl | null = null

    constructor(p_: IDropdownProps) {
        super(p_)

        this.state = {
            active: false,
            inputValue: '',
            selected: {
                id: '',
                name: null,
            },
            selectedList: p_.list ? p_.list : null,
            opt: getFilteredList(p_.options, p_.list),
            position: 'bottom',
            positionChanged: false,
        }

        this._onToggleClick = this._onToggleClick.bind(this)
        this._onChange = this._onChange.bind(this)
        this.searchableChange = this.searchableChange.bind(this)
        this.roundedMod = this.getRoundedMod(p_.rounded)
        this.filtered = []
    }

    isActive() {
        const p_ = this.props
        const s_ = this.state

        return typeof p_.active === 'boolean' ? p_.active : s_.active
    }

    hide() {
        const p_ = this.props
        if (p_.searchable && !p_.multiselect && !p_.multiselectItems) {
            if (!this.state.inputValue || !this.state.inputValue.length || this.filtered.length === 0) {
                const selectedName = this.state.selected.name
                this.setState({
                    inputValue:
                        typeof selectedName === 'string' || typeof selectedName === 'number'
                            ? String(selectedName)
                            : '',
                })
            }
        }
        this.setState(
            {
                active: false,
            },
            () => {
                if (p_.onHide) {
                    p_.onHide()
                }
            }
        )
    }

    show() {
        const p_ = this.props

        if (!p_.multiselect && !p_.multiselectItems && p_.searchable && !p_.noFocus) {
            if (this.searchable) {
                this.searchable!.focus()
            }

            this.setState({ inputValue: '' })
        }
        this.setState(
            {
                active: true,
            },
            () => {
                if (p_.onShow) {
                    p_.onShow()
                }
                this.setAutoPosition()
            }
        )
    }
    setAutoPosition = () => {
        const s_ = this.state
        const p_ = this.props

        if (p_.isRelativeList) {
            if (!s_.positionChanged) {
                this.setState({ position: 'bottom', positionChanged: true })
            }
            return
        }

        if (!s_.positionChanged) {
            let position: IState['position'] = 'bottom'
            const dropdown = (this as unknown) as DropdownMethods & {
                dropdownContent: HTMLDivElement | null
                customContent: HTMLDivElement | null
            }
            const contentElements = [dropdown.dropdownContent, dropdown.customContent]

            contentElements.forEach((contentElement) => {
                if (contentElement) {
                    const offsetTopHeight = contentElement.clientHeight + contentElement.getBoundingClientRect().top
                    const height = document.body.clientHeight

                    if (offsetTopHeight > height) {
                        position = 'top'
                    }
                }
            })

            this.setState({ position, positionChanged: true })
        }
    }
    removeTagItem(name: string) {
        const s_ = this.state
        const p_ = this.props
        const removedItemList = (s_.selectedList || []).filter((item) => item.name !== name)
        const newItem = Object.assign({}, { name: name, label: name })

        this.setState({ selectedList: removedItemList, opt: [...s_.opt, newItem] })
        if (p_.onChange) {
            p_.onChange(removedItemList, null, name)
        }
    }
    filterOptions = (value: string) => {
        const p_ = this.props
        const { opt } = this.state
        if (p_.notFilterOnSearch) {
            return opt
        }

        const searchableValue = p_.mask ? value.split('_')[0] : value
        const filtered = opt.filter((field) => {
            const label = getDropdownOptionField(field, p_.labelField)
            return typeof label === 'string' && label.toLowerCase().indexOf(searchableValue.toLowerCase()) !== -1
        })

        if (this.props.showCustomOption) {
            const customOption = this.props.options.find((item) => item.id === 'custom')
            if (customOption) {
                filtered.push(customOption)
            }
        }

        return filtered
    }
    searchableChange(e: React.ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const p_ = this.props
        let filtered = this.filterOptions(value)

        if (p_.multiselect || p_.multiselectItems) {
            if (value.length > 2 && filtered.length > 0) {
                this.show()
            } else {
                this.hide()
            }
        } else {
            this.show()
        }

        if (p_.config && p_.config.pattern) {
            if (p_.config.pattern.test(value)) {
                this.setState({ inputValue: value })
                if (p_.onBlur) {
                    p_.onBlur({ value })
                }
            }
        } else {
            this.setState({ inputValue: value })
        }
    }
    searchableBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const p_ = this.props
        const value = e.target.value

        if (p_.onBlur) {
            p_.onBlur({ value }, e)
        }

        if (p_.placeholderAsValue) {
            setTimeout(() => {
                this.setState({ inputValue: '' })
            }, 0)
        } else {
            this.setState({ inputValue: value })
        }
    }

    handleClickOutside() {
        if (this.isActive()) {
            this.hide()
        }
    }

    _onToggleClick(event: React.MouseEvent) {
        event.preventDefault()
        if (this.props.type === 'hmsTime') {
            return
        }
        if (this.props.disabled) {
            return
        }
        if (this.isActive()) {
            this.hide()
        } else {
            this.show()
        }
    }

    _onToggleClickhHms(event: React.MouseEvent) {
        event.preventDefault()
        if (this.props.disabled) {
            return
        }
        if (this.isActive()) {
            this.hide()
        } else {
            this.show()
        }
    }
    _onToggleClickhHmsMode(event: React.MouseEvent, p_: IDropdownProps, scheduleMode?: boolean) {
        if (scheduleMode) {
            this.searchable?.focus()
            if (!p_.multiselect && !p_.multiselectItems && !p_.disabled) {
                const value = p_.placeholderAsValue ? p_.value : ''
                this.setState({
                    inputValue: typeof value === 'string' || typeof value === 'number' ? String(value) : '',
                })
            }
        } else {
            event.preventDefault()
            if (this.props.disabled) {
                return
            }
            if (this.isActive()) {
                this.hide()
            } else {
                this.show()
            }
        }
    }

    _onChange(e: React.MouseEvent, value: DropdownOption) {
        const p_ = this.props
        this._onToggleClick(e)
        if (p_.onChange) {
            p_.onChange(value)
        }
        this._selectedOption(p_)
    }

    _selectedOption(p_: IDropdownProps) {
        if (p_.options && !(p_.value === undefined || p_.value === null)) {
            p_.options.forEach((item) => {
                const itemValue = getDropdownOptionField(item, p_.valueField)
                const itemLabel = getDropdownOptionField(item, p_.labelField)
                if (itemValue === p_.value) {
                    this.setState({
                        selected: {
                            id: itemValue as DropdownValue,
                            name: itemLabel as React.ReactNode,
                        },
                    })
                    if (!p_.multiselect && !p_.multiselectItems && p_.searchable) {
                        this.setState({
                            inputValue:
                                typeof itemLabel === 'string' || typeof itemLabel === 'number' ? String(itemLabel) : '',
                        })
                    }
                }
            })
        } else {
            if (p_.onlyPlaceholder) {
                return
            }

            this.setState({
                selected: {
                    id: undefined,
                    name: p_.placeholder,
                },
            })
            if (!p_.multiselect && !p_.multiselectItems && p_.searchable) {
                this.setState({ inputValue: '' })
            }
        }
    }

    _selectedMulti(option: DropdownOption) {
        const s_ = this.state
        const p_ = this.props
        const deleteDuplicate = s_.opt.filter((item) => item.name !== option.name)
        const newList = s_.selectedList ? [...s_.selectedList, { ...option }] : []

        this.setState({
            selectedList: newList,
            inputValue: '',
            active: false,
            opt: [...deleteDuplicate],
        })
        if (p_.onChange) {
            p_.onChange(newList, option)
        }
    }

    getRoundedMod(rounded: string) {
        const splitRounded = rounded.split('_')
        let roundedMod = splitRounded[splitRounded.length - 1]
        if (roundedMod === 'none') {
            roundedMod = 'mini'
        }
        return roundedMod
    }

    componentDidMount() {
        const p_ = this.props

        if (typeof p_.value !== 'undefined' && p_.options) {
            this._selectedOption(p_)
        }
        if (p_.multiselect || (p_.multiselectItems && p_.list && p_.options)) {
            this.setState({
                opt: getFilteredList(p_.options, p_.list),
                selectedList: p_.list,
            })
        }

        document.addEventListener('keydown', this.onEnterPressed)
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.onEnterPressed)
    }
    componentDidUpdate(prevProps: IDropdownProps, prevState: IState) {
        const p_ = this.props
        if (p_.options && (!deepEqual(p_.value, prevProps.value) || !deepEqual(p_.options, prevProps.options))) {
            this._selectedOption(p_)
        }
        this.roundedMod = this.getRoundedMod(p_.rounded)

        const opt = getFilteredList(p_.options, p_.list)
        const selectedList = p_.list ? p_.list : null

        if (!deepEqual(prevState.selectedList, selectedList) || !deepEqual(prevState.opt, opt)) {
            this.setState({
                selectedList,
                opt,
            })
        }
    }

    onEnterPressed = (event: KeyboardEvent) => {
        const s_ = this.state
        const p_ = this.props

        if (p_.disableAddNewTags) {
            return
        }

        const target = event.target
        if (event.key === 'Enter' && target instanceof HTMLElement && target.id === 'autosize_input') {
            const p_ = this.props
            if (p_.multiselect || (p_.multiselectItems && p_.searchable)) {
                event.preventDefault()
                if (
                    (s_.selectedList || []).filter((item) => item.name === s_.inputValue).length === 0 &&
                    // && s_.inputValue.length > 1
                    p_.onValidateSymbolNumber
                        ? s_.inputValue.length > 2
                        : s_.inputValue.length > 1
                ) {
                    const newList = s_.selectedList
                        ? [...s_.selectedList, { name: s_.inputValue, label: s_.inputValue }]
                        : [{ name: s_.inputValue, label: s_.inputValue }]
                    const deleteDuplicate = s_.opt.filter((item) => item.name !== s_.inputValue)

                    this.setState({
                        selectedList: newList,
                        inputValue: '',
                        active: false,
                        opt: [...deleteDuplicate],
                    })
                    if (p_.onChange) {
                        p_.onChange(newList)
                    }
                    if (p_.onValidateSymbolNumber) {
                        p_.onValidateSymbolNumber(false)
                    }
                } else if (s_.inputValue.length < 3) {
                    if (p_.onValidateSymbolNumber) {
                        p_.onValidateSymbolNumber(true)
                    }
                }
            }
        }
    }
}

export default DropdownMethods
